import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Button, message, Modal, Tooltip } from 'antd';
import posthog from 'posthog-js';
import ReduxDataTable from '../redux_data_table/ReduxDataTable';
import { EmptyState } from '../empty_state/EmptyState';
import AssetWithQRCodeDisplay from '../asset_row_display/AssetWithQRCodeDisplay';
import { ASSETS_CRUD_ACTION_CREATORS } from '../../actions/assets_actions';
import { LINK_SUB_ASSET_MODAL_TARGET_COLLECTION } from '../../reducers/assets_reducer';
import {
	assetsRestCrudThunksForBuyer,
	assetsRestCrudThunksForSupplier,
	assetsSearchAndAPIAdaptor,
	linkSubAssetForBuyer,
	linkSubAssetForSupplier,
} from '../../thunks/assets_thunks';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { FILTER_CONFIG_NAMES, ROLE_TYPES } from '../../utils/DataConstants';
import {
	getBuyerCompanySettingValue,
	isBuyerAllowedToAccess,
	PERMISSION_NAMES,
} from '../../utils/AuthUtils';
import { INITIAL_ACTIVE_FILTERS } from '../../utils/FilterUtils';
import { PAGE_SIZE_OPTIONS } from '../../constants/pagination';
import { useFilters } from '../../hooks/useFilters';
import {
	ASSET_SORTERS,
	getAssetFilterConfig,
	getSupplierAssetFilterConfig,
} from '../../utils/AssetFilterUtils';
import { withUserType } from '../withUserType';

// Isolated overview collection (asset list + selections + active filters), which
// must be registered in the assets reducer or the fetch dispatch reads an
// undefined slice. The filter-option dropdowns reuse the shared
// `assetsAdvancedFilters` collection — the same one the assets index uses — which
// is already registered across every entity reducer those filters fetch from
// (locations, areas, asset_types, …). Inventing a new name here would crash the
// per-filter option fetches (e.g. the location filter reading state.locations).
const MODAL_TC = LINK_SUB_ASSET_MODAL_TARGET_COLLECTION;
const MODAL_ADVANCED_FILTERS_TC = 'assetsAdvancedFilters';

interface LinkSubAssetModalProps {
	// The asset the selected assets will be linked under (as sub assets).
	parentAsset: any;
	onCancel: () => void;
	// Called after a successful link — the tab uses this to refetch + close.
	onSuccess: () => void;
	// Injected by connect.
	userType?: string;
	assets?: any;
	currentUser?: any;
	companyConfig?: any;
	linkSubAssets?: (parentAssetId: number, assetIds: React.Key[]) => Promise<any>;
	updateAssetsFilters?: (filters: any, targetCollection: string) => void;
}

const LinkSubAssetModal: FC<LinkSubAssetModalProps> = ({
	parentAsset,
	onCancel,
	onSuccess,
	userType,
	assets,
	currentUser,
	companyConfig,
	linkSubAssets,
	updateAssetsFilters,
}) => {
	const isSupplier = userType === ROLE_TYPES.SUPPLIER;
	// Coerce to a number: when the parent asset hasn't hydrated from Redux yet the
	// caller falls back to `{ id: match.params.id }`, which is a string. API records
	// carry a numeric id, so without this the `record.id === parentAssetId` guards
	// below would compare number === string and never disable the parent / its
	// existing children.
	const rawParentAssetId = nullSafeGet('id', parentAsset);
	const parentAssetId =
		rawParentAssetId == null ? rawParentAssetId : parseInt(rawParentAssetId, 10);
	// The backend requires every sub asset to share the parent's location, so
	// scope the picker to that location and never show ineligible candidates.
	const parentLocationId =
		nullSafeGet('locationId', parentAsset) || nullSafeGet('location.id', parentAsset);

	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const [loading, setLoading] = useState(false);

	const columns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'name',
				render: (text, record) => <AssetWithQRCodeDisplay asset={record} disableActions />,
			},
		],
		[]
	);

	const allowArea = getBuyerCompanySettingValue(
		isSupplier ? ROLE_TYPES.SUPPLIER : ROLE_TYPES.BUYER
	)(
		'assetConfig.allowAreaAssociationWithAssets',
		isSupplier ? {} : companyConfig,
		isSupplier ? currentUser : {},
		false
	);

	const removeAssetNumberToQRCodeAssociation = getBuyerCompanySettingValue(
		isSupplier ? ROLE_TYPES.SUPPLIER : ROLE_TYPES.BUYER
	)(
		'assetConfig.removeAssetNumberToQRCodeAssociation',
		isSupplier ? {} : companyConfig,
		isSupplier ? currentUser : {},
		false
	);

	const replacementIndexEnabled =
		!isSupplier &&
		!!posthog.isFeatureEnabled('replacement-index') &&
		isBuyerAllowedToAccess(
			PERMISSION_NAMES.VIEW_REPLACEMENT_INDEX,
			companyConfig,
			nullSafeGet('roles', currentUser)
		);

	const updateFilters = useCallback(
		(filters) => updateAssetsFilters(filters, MODAL_TC),
		[updateAssetsFilters]
	);

	const allFilters = useFilters({
		filterTargetCollectionName: MODAL_ADVANCED_FILTERS_TC,
		overviewCollectionName: MODAL_TC,
		updateFilters,
	});

	const filterConfig = useMemo(() => {
		const config = isSupplier
			? getSupplierAssetFilterConfig({
					filters: allFilters,
					allowArea,
					currentUser,
					removeAssetNumberToQRCodeAssociation,
			  })
			: getAssetFilterConfig({
					filters: allFilters,
					allowArea,
					currentUser,
					removeAssetNumberToQRCodeAssociation,
					replacementIndexEnabled,
			  });
		// Location is fixed to the parent's. Mark the Location filter allowChanging:
		// false so it still shows as an applied pill but can't be removed or edited
		// (see AdvancedFilters / SelectedFilters), keeping users from pulling in
		// cross-location assets that would only fail backend validation on submit.
		return parentLocationId
			? config.map((entry) =>
					nullSafeGet('fieldName', entry) === FILTER_CONFIG_NAMES.LOCATION
						? { ...entry, allowChanging: false }
						: entry
			  )
			: config;
	}, [
		isSupplier,
		allFilters,
		allowArea,
		currentUser,
		removeAssetNumberToQRCodeAssociation,
		replacementIndexEnabled,
		parentLocationId,
	]);

	const restCrudThunks = isSupplier
		? assetsRestCrudThunksForSupplier
		: assetsRestCrudThunksForBuyer;

	const fetchDataProp = useMemo(
		() =>
			posthog.isFeatureEnabled('assets-openwsearch')
				? {
						...restCrudThunks,
						read: (
							params,
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						) =>
							restCrudThunks.searchWithPostMethod(
								filters ? { ...(params || {}), ...filters } : params,
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection,
								assetsSearchAndAPIAdaptor
							),
				  }
				: restCrudThunks,
		[restCrudThunks]
	);

	// Which rows can be linked. Sub assets are a single level deep, so exclude:
	// the parent itself, assets already linked to this parent, and assets that
	// already own sub assets (nesting them would create a 3-level hierarchy the
	// backend rejects). Assets linked to a *different* parent are allowed — the
	// backend intentionally re-parents them. Cross-location assets never appear
	// here because the table is pre-filtered to the parent's location.
	const getDisabledReason = useCallback(
		(record) => {
			const recordId = parseInt(nullSafeGet('id', record), 10);
			const recordParentId = parseInt(nullSafeGet('parentId', record), 10);
			if (recordId === parentAssetId) {
				return 'This is the current asset';
			}
			if (recordParentId === parentAssetId) {
				return 'Already a sub asset of this asset';
			}
			if (nullSafeGetOrElse('subAssetCount', record, 0) > 0) {
				return 'This asset already has its own sub assets';
			}
			return null;
		},
		[parentAssetId]
	);

	const rowSelection = useMemo(
		() => ({
			selectedRowKeys,
			onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
			getCheckboxProps: (record) => ({ disabled: !!getDisabledReason(record) }),
			// A native `title` on a disabled checkbox is unreliable (browsers often
			// suppress tooltips on disabled controls), so wrap the checkbox in a
			// Tooltip. The <span> ensures hover still fires over the disabled input.
			renderCell: (_checked, record, _index, originNode) => {
				const disabledReason = getDisabledReason(record);
				return disabledReason ? (
					<Tooltip title={disabledReason} placement="right">
						<span className="inline-flex">{originNode}</span>
					</Tooltip>
				) : (
					originNode
				);
			},
		}),
		[selectedRowKeys, getDisabledReason]
	);

	const onSubmit = useCallback(() => {
		if (!selectedRowKeys.length) {
			return;
		}
		setLoading(true);
		linkSubAssets(parentAssetId, selectedRowKeys)
			.then(() => {
				setSelectedRowKeys([]);
				onSuccess();
			})
			.catch((error) => {
				message.error(typeof error === 'string' ? error : 'Failed to link sub assets');
			})
			.finally(() => setLoading(false));
	}, [selectedRowKeys, linkSubAssets, parentAssetId, onSuccess]);

	return (
		<Modal
			width="80%"
			visible
			title="Link Sub Assets"
			closable
			onCancel={onCancel}
			footer={<Button onClick={onCancel}>Cancel</Button>}
			// Matches the Linked Work Orders modal's grey body (their .less sets the
			// same #f0f4f5). Applied via bodyStyle so it works without a separate
			// stylesheet the dev-server watcher may miss.
			bodyStyle={{ backgroundColor: '#f0f4f5', padding: 16 }}
		>
			<ReduxDataTable
				filterConfig={filterConfig}
				entityCollectionName="assets"
				collectionName="assets"
				targetCollectionName={MODAL_TC}
				stateSlice={assets}
				fetchData={fetchDataProp}
				tableColumns={columns}
				keyAccessor={(el) => el.id}
				showHeader={false}
				rowSelection={rowSelection}
				preAppliedFilters={parentLocationId ? { locationIds: [parentLocationId] } : {}}
				// Seed the location as an active filter too, so it renders as a (locked)
				// pill; preAppliedFilters above still hard-enforces it on every fetch.
				initialFilters={
					parentLocationId
						? { ...INITIAL_ACTIVE_FILTERS, locationIds: String(parentLocationId) }
						: { ...INITIAL_ACTIVE_FILTERS }
				}
				initialPagination={{ current: 1, pageSize: 10 }}
				hiddenPagination={{ showSizeChanger: true, pageSizeOptions: PAGE_SIZE_OPTIONS }}
				initialSorting={{ sort_by: 'name', order: 'ascend' }}
				sorters={posthog.isFeatureEnabled('assets-openwsearch') ? ASSET_SORTERS : undefined}
				rightActions={
					<Button
						type="primary"
						size="large"
						loading={loading}
						disabled={!selectedRowKeys.length}
						onClick={onSubmit}
					>
						Link Selected Sub Assets
						{selectedRowKeys.length ? ` (${selectedRowKeys.length})` : ''}
					</Button>
				}
				emptyState={<EmptyState headline="No assets" />}
			/>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	assets: state.assets,
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	linkSubAssets: (parentAssetId, assetIds) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? linkSubAssetForSupplier(parentAssetId, assetIds)
				: linkSubAssetForBuyer(parentAssetId, assetIds)
		),
	updateAssetsFilters: (filters, targetCollection) =>
		dispatch(ASSETS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
});

const ConnectedLinkSubAssetModal = connect(mapStateToProps, mapDispatchToProps)(LinkSubAssetModal);

// withUserType feeds `userType` down as a prop so mapDispatchToProps can pick the
// role-appropriate link thunk.
export default withUserType(ConnectedLinkSubAssetModal);
